
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Send, Loader2 } from 'lucide-react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

const formSchema = z.object({
  email: z.string()
    .email("Please enter a valid email address")
    .refine(email => email.length > 0, "Email is required"),
  calculatorDescription: z.string()
    .min(20, "Description must be at least 20 characters long")
    .max(500, "Description must not exceed 500 characters")
    .refine(
      (text) => {
        const words = text.split(/\s+/).filter(word => word.length > 0);
        const hasReasonableWords = words.every(word => word.length <= 30);
        return words.length >= 3 && hasReasonableWords;
      },
      "Please provide a coherent description with at least 3 words"
    ),
  /** Honeypot field — must remain empty. Bots will auto-fill this. */
  website: z.string().max(0, "").optional(),
});

type FormData = z.infer<typeof formSchema>;

interface SubmissionFormProps {
  onSubmit: (data: { email: string; calculatorDescription: string }) => Promise<void>;
  isSubmitting: boolean;
  isDisabled: boolean;
}

/**
 * Calculator request submission form with honeypot anti-spam protection.
 * The hidden "website" field catches bots that auto-fill all fields.
 */
const SubmissionForm: React.FC<SubmissionFormProps> = ({ onSubmit, isSubmitting, isDisabled }) => {
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      calculatorDescription: "",
      website: "",
    },
  });

  const handleFormSubmit = async (data: FormData) => {
    // If honeypot field is filled, silently ignore (it's a bot)
    if (data.website && data.website.length > 0) {
      form.reset();
      return;
    }

    try {
      await onSubmit({ email: data.email, calculatorDescription: data.calculatorDescription });
      form.reset();
    } catch (error) {
      console.error("Error in form submission:", error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  placeholder="Your email"
                  type="email"
                  {...field}
                  className="w-full"
                  disabled={isSubmitting || isDisabled}
                  aria-label="Email address for calculator request"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Honeypot field — hidden from real users, bots will fill it */}
        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem className="absolute opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
              <FormControl>
                <Input
                  placeholder="Website"
                  type="text"
                  {...field}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="calculatorDescription"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea
                  placeholder="Describe the calculator you'd like to see..."
                  className="w-full min-h-[100px]"
                  {...field}
                  disabled={isSubmitting || isDisabled}
                  aria-label="Calculator description"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button 
          type="submit" 
          className="w-full flex items-center justify-center gap-2"
          disabled={isSubmitting || isDisabled}
          aria-label={!isDisabled ? "Submit calculator request" : "Submission temporarily disabled"}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Submit Request
            </>
          )}
        </Button>
      </form>
    </Form>
  );
};

export default SubmissionForm;
